import React, { useMemo, useState } from 'react';
import { Card, Space } from 'antd';

interface PieDataItem {
  name: string;
  value: number; // 百分比数值
  color: string;
  count?: number; // 订单数量
  profit?: number; // 利润
  amount?: number; // 实际金额
}

interface PieChartProps {
  data?: PieDataItem[];
  loading?: boolean;
  title?: string;
}

const PieChart: React.FC<PieChartProps> = ({
  data = [],
  loading = false,
  title = '业务构成分析'
}) => {
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  // 模拟数据
  const mockData: PieDataItem[] = [
    { name: '商标注册', value: 35, color: '#1f1f1f' },
    { name: '商标交易', value: 25, color: '#ff6b35' },
    { name: '商标仅转让', value: 20, color: '#4285f4' },
    { name: '公司注册', value: 15, color: '#34a853' },
    { name: '异形商标注册', value: 5, color: '#9c27b0' },
  ];

  const chartData = data.length > 0 ? data : mockData;
  const total = chartData.reduce((sum, item) => sum + item.value, 0);

  // 计算总金额和总订单数
  const totalAmount = chartData.reduce((sum, item) => sum + (item.amount || 0), 0);
  const totalOrders = chartData.reduce((sum, item) => sum + (item.count || 0), 0);

  // 计算每个扇形的角度
  let currentAngle = 0;
  const sectors = useMemo(() => {
    return chartData.map(item => {
      const percentage = total > 0 ? (item.value / total) * 100 : 0;
      const angle = total > 0 ? (item.value / total) * 360 : 0;
      const startAngle = currentAngle;
      const endAngle = currentAngle + angle;
      currentAngle += angle;

      return {
        ...item,
        percentage,
        startAngle,
        endAngle,
        angle
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartData, total]);

  const activeSector = hoverIndex !== null && hoverIndex < sectors.length ? sectors[hoverIndex] : null;

  // 创建圆环SVG路径
  const createDonutPath = (centerX: number, centerY: number, innerRadius: number, outerRadius: number, startAngle: number, endAngle: number) => {
    const startOuter = polarToCartesian(centerX, centerY, outerRadius, endAngle);
    const endOuter = polarToCartesian(centerX, centerY, outerRadius, startAngle);
    const startInner = polarToCartesian(centerX, centerY, innerRadius, endAngle);
    const endInner = polarToCartesian(centerX, centerY, innerRadius, startAngle);

    const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";

    return [
      "M", startOuter.x, startOuter.y,
      "A", outerRadius, outerRadius, 0, largeArcFlag, 0, endOuter.x, endOuter.y,
      "L", endInner.x, endInner.y,
      "A", innerRadius, innerRadius, 0, largeArcFlag, 1, startInner.x, startInner.y,
      "Z"
    ].join(" ");
  };

  const polarToCartesian = (centerX: number, centerY: number, radius: number, angleInDegrees: number) => {
    const angleInRadians = (angleInDegrees - 90) * Math.PI / 180.0;
    return {
      x: centerX + (radius * Math.cos(angleInRadians)),
      y: centerY + (radius * Math.sin(angleInRadians))
    };
  };

  const renderPieChart = () => {
    const centerX = 120;
    const centerY = 120;
    const outerRadius = 100;
    const innerRadius = 60; // 增加内圆半径，使圆环更厚

    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <svg width="240" height="240" style={{ overflow: 'visible' }}>
          {sectors.map((sector, index) => {
            // 创建圆环路径
            const donutPath = createDonutPath(centerX, centerY, innerRadius, outerRadius, sector.startAngle, sector.endAngle);

            return (
              <path
                key={index}
                d={donutPath}
                fill={sector.color}
                stroke="#fff"
                strokeWidth="2"
                style={{
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                  opacity: hoverIndex === null || hoverIndex === index ? 1 : 0.35,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.05)';
                  e.currentTarget.style.transformOrigin = `${centerX}px ${centerY}px`;
                  setHoverIndex(index);
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                  setHoverIndex(null);
                }}
              />
            );
          })}

          {/* 中心圆 */}
          <circle
            cx={centerX}
            cy={centerY}
            r={innerRadius}
            fill="#fff"
            stroke="#f0f0f0"
            strokeWidth="1"
          />

          {/* 中心文本 */}
          <text
            x={centerX}
            y={centerY - 10}
            textAnchor="middle"
            fontSize="14"
            fontWeight="bold"
            fill="#333"
          >
            {activeSector ? activeSector.name : '总金额'}
          </text>
          <text
            x={centerX}
            y={centerY + 5}
            textAnchor="middle"
            fontSize="16"
            fontWeight="bold"
            fill="#1890ff"
          >
            {activeSector ? `${activeSector.percentage.toFixed(1)}%` : `¥${totalAmount.toLocaleString()}`}
          </text>
          <text
            x={centerX}
            y={centerY + 20}
            textAnchor="middle"
            fontSize="12"
            fill="#666"
          >
            {activeSector
              ? `${activeSector.count ?? 0}单${activeSector.amount ? ` / ¥${activeSector.amount.toLocaleString()}` : ''}`
              : `${totalOrders}单`}
          </text>
        </svg>
      </div>
    );
  };

  const renderLegend = () => (
    <div style={{ display: 'flex', flexWrap: 'wrap', rowGap: 8, columnGap: 16, justifyContent: 'center', marginTop: 16 }}>
      {chartData.map((item, index) => {
        // 如果数据中已经包含百分比，则直接使用，否则计算
        const percentage = typeof item.value === 'number' && item.value <= 100 && total <= 100
          ? item.value.toFixed(1)
          : ((item.value / total) * 100).toFixed(1);

        return (
          <div
            key={index}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              padding: '2px 8px',
              borderRadius: 6,
              background: hoverIndex === index ? '#f0f5ff' : 'transparent',
              border: hoverIndex === index ? '1px solid #d6e4ff' : '1px solid transparent',
              cursor: 'pointer'
            }}
            onMouseEnter={() => setHoverIndex(index)}
            onMouseLeave={() => setHoverIndex(null)}
          >
            <div style={{
              width: 12,
              height: 12,
              backgroundColor: item.color,
              borderRadius: '50%',
            }} />
            <span style={{ fontSize: 14, color: '#666' }}>
              {item.name}
            </span>
            <span style={{ fontSize: 14, fontWeight: 'bold', color: '#333' }}>
              {percentage}%
            </span>
            {item.amount && (
              <span style={{ fontSize: 12, color: '#1890ff', marginLeft: '4px' }}>
                ¥{item.amount.toLocaleString()}
              </span>
            )}
            {item.count && (
              <span style={{ fontSize: 12, color: '#999', marginLeft: '4px' }}>
                ({item.count}单)
              </span>
            )}
          </div>
        );
      })}
    </div>
  );

  return (
    <Card
      loading={loading}
      title={title}
      style={{ height: 550 }}
      extra={
        <Space>
          <span style={{ color: '#666', fontSize: 12 }}>{title === '类目分布' ? '商标业务' : ''}</span>
        </Space>
      }
    >
      {renderPieChart()}
      {renderLegend()}
    </Card>
  );
};

export default PieChart;
